import React, { ComponentProps, FC, useCallback, useState } from 'react';

import styled from 'styled-components';

import {
  Account,
  ENTITLEMENTS_PLATFORM_TYPES,
  EntitlementsProject,
} from '@adsk/alloy-provider';
import { Box, Text } from '@adsk/alloy-react-box';
import { BasicButton } from '@adsk/alloy-react-button';
import {
  LogoAutodeskBim360Icon,
  LogoAutodeskAccIcon,
  TriangleUpIcon,
  TriangleDownIcon,
} from '@adsk/alloy-react-icon';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme, { flattenStyle } from '@adsk/alloy-react-theme';
import Tooltip, { Flyout, OverflowTooltip } from '@adsk/alloy-react-tooltip';

import ProjectLoader from './ProjectLoader';
import ProjectPicker from './ProjectPicker';
import { useTranslation } from '../i18n';

const StyledCurrentTemplateLabel = styled(Box)`
  ${{
    [theme.mediaQuery.medium]: {
      maxWidth: 100,
    },
  }}
`;

const StyledProjectAccountLabel = styled(Box)`
  ${{
    [theme.mediaQuery.medium]: {
      maxWidth: '100px',
    },
    [theme.mediaQuery.small]: {
      display: 'none',
    },
  }}
`;

type ProjectButtonProps = {
  accounts?: Account[];
  projectId?: string;
  accountId?: string;
  productId?: string;
  currentProject?: EntitlementsProject;
  onAccountClick?: (account?: object, url?: string) => void;
  onProjectClick?: (project?: object, url?: string) => void;
  renderProjectPicker?: (
    props: ComponentProps<typeof ProjectPicker>,
  ) => JSX.Element;
};

/**
 * Acs ProjectButton
 */
const ProjectButton: FC<ProjectButtonProps> = ({
  accounts,
  accountId,
  projectId,
  productId,
  currentProject,
  onAccountClick,
  onProjectClick,
  renderProjectPicker = (props: ComponentProps<typeof ProjectPicker>) => (
    <ProjectPicker {...props} />
  ),
}) => {
  const { t } = useTranslation();

  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);
  const openMenu = useCallback(() => {
    setMenuOpen(true);
  }, []);
  const toggleMenu = useCallback(() => {
    setMenuOpen((v) => !v);
  }, []);
  const currentAccount =
    (accounts &&
      accountId &&
      accounts.find((p) => p.account_id === accountId)) ||
    undefined;
  const currentTemplate =
    currentProject?.classification === 'template' ? currentProject : undefined;

  const LogoComponent =
    !currentTemplate && currentProject
      ? currentProject.platform === ENTITLEMENTS_PLATFORM_TYPES.ACC
        ? LogoAutodeskAccIcon
        : LogoAutodeskBim360Icon
      : undefined;

  const TriangleIconComponent = menuOpen ? TriangleUpIcon : TriangleDownIcon;

  return currentProject || currentAccount || currentTemplate ? (
    <Flyout
      show={menuOpen}
      onHide={closeMenu}
      onShow={openMenu}
      style={{ maxWidth: 'fit-content' }}
      offset={15}
      renderBody={(props) => <Tooltip.Body {...props} padding={0} />}
      placement={Flyout.PLACEMENTS.BOTTOM}
      renderTarget={(props) => (
        <Tooltip.Target
          {...props}
          style={{ display: 'flex', ...flattenStyle(props.style) }}
        />
      )}
      content={renderProjectPicker({
        currentAccountId: accountId,
        accounts,
        currentProjectId: projectId,
        currentProductId: productId,
        onClose: closeMenu,
        onAccountClick,
        onProjectClick,
      })}
    >
      <BasicButton
        onClick={toggleMenu}
        data-testid="TopNavigation__projects"
        style={{
          padding: 0,
          justifyContent: 'start',
          color: theme.colors.charcoal900,
          '&:hover': {
            color: theme.colors.charcoal700,
          },
        }}
      >
        {!currentTemplate && (
          <Box
            display="inline-block"
            overflow="hidden"
            flexShrink={0}
            borderRadius="4px"
            width="32px"
            height="32px"
          >
            <img
              alt={currentProject?.name || currentAccount?.account_display_name}
              src={currentProject?.imageUrl || currentAccount?.image_url}
              data-testid="project-image"
              width={32}
              height={32}
            />
          </Box>
        )}

        {/*  Template Only */}
        {currentTemplate && (
          <>
            <Text
              typography="bodyMedium"
              color="charcoal500"
              marginRight="12px"
            >
              {t('PROJECT_TEMPLATE')}
            </Text>
            <StyledCurrentTemplateLabel
              typography="bodyMediumBold"
              color="charcoal900"
              marginRight="8px"
              maxWidth="300px"
            >
              <OverflowTooltip content={currentTemplate?.name}>
                <span>{currentTemplate?.name}</span>
              </OverflowTooltip>
            </StyledCurrentTemplateLabel>
          </>
        )}

        {!currentTemplate && currentProject && (
          <StyledProjectAccountLabel
            textAlign="left"
            marginLeft="15px"
            maxWidth="300px"
          >
            {/*  Project And Account */}
            {currentAccount && (
              <Box typography="caption" color="charcoal700" textAlign="left">
                <OverflowTooltip content={currentAccount.account_display_name}>
                  <span>{currentAccount.account_display_name}</span>
                </OverflowTooltip>
              </Box>
            )}
            {/*  Project Only */}
            <Box typography="bodyMediumBold">
              <OverflowTooltip content={currentProject?.name}>
                <span>{currentProject?.name}</span>
              </OverflowTooltip>
            </Box>
          </StyledProjectAccountLabel>
        )}

        {/*  Account Only */}
        {!currentProject && currentAccount && (
          <StyledProjectAccountLabel
            textAlign="left"
            marginLeft="15px"
            maxWidth="300px"
          >
            <Box typography="bodyMediumBold">
              <OverflowTooltip content={currentAccount.account_display_name}>
                <span>{currentAccount.account_display_name}</span>
              </OverflowTooltip>
            </Box>
          </StyledProjectAccountLabel>
        )}

        {LogoComponent && (
          <LogoComponent
            size={16}
            style={{
              margin: `0px 4px -${!currentAccount ? 2 : 13}px 9px`,
              color: theme.colors.charcoal700,
              [theme.mediaQuery.small]: {
                display: 'none',
              },
            }}
          />
        )}

        <TriangleIconComponent
          style={{
            color: theme.colors.charcoal700,
            marginBottom: currentProject && currentAccount ? '-12px' : 0,

            [theme.mediaQuery.small]: {
              margin: '0 4px',
            },
          }}
          size={20}
        />
      </BasicButton>
    </Flyout>
  ) : (
    <ProjectLoader />
  );
};

ProjectButton.displayName = 'ProjectButton';

export default WithAlloySuspense(ProjectButton);
